import { RedirectToSignIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { usePostHog } from '~/posthog';
import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { Toast } from 'primereact/toast';
import { createContext, useContext, useEffect, useState } from 'react';
import { authAtom, jotaiStore } from '~/atoms/authAtom';
import { globalSearchRefAtom } from '~/atoms/globalSearch';
import { toastAtom } from '~/atoms/toastAtom';
import { useSessionConflictNotification } from '~/hooks/useSessionConflictNotification';
import {
  GlobalSearchPopup,
  GlobalSearchPopupRef,
} from '~/components/ui/globalSearch/GlobalSearchPopup';
import { SidebarStaticV2 } from '~/components/ui/layout/navigation/sidebarStatic/SidebarStaticV2';
import { SidebarStatic } from '~/components/ui/layout/navigation/sidebarStatic/SidebarStatic';
import { canAccess } from '~/utils/rbac';
import type { Role } from '~/types/rbac';
import { useFeatureFlagsFromClerk } from '~/hooks/useOrganizationConfig';
import { isFeatureEnabled } from '~/config/organizationConfig';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

import { useWorkflowsSimple } from '~/lib/workflows/useWorkflowsSimple';

const WorkflowsNotificationsInitializer = () => {
  useWorkflowsSimple();
  return null;
};

interface SidebarContextType {
  isExpanded: boolean;
  sidebarWidth: number;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const Route = createFileRoute('/_authLayout')({
  component: AuthLayoutComponent,
  beforeLoad: async () => {
    const auth = jotaiStore.get(authAtom);
    if (auth?.auth?.isLoaded) {
      if (!auth.auth.isSignedIn) {
        throw redirect({ to: '/sign-in' });
      }
      if (
        !canAccess('fund') &&
        !canAccess('portfolio') &&
        !canAccess('dashboard') &&
        !canAccess('document') &&
        !canAccess('workflow') &&
        !canAccess('setting') &&
        !canAccess('user')
      ) {
        throw notFound();
      }
      return;
    }
    return;
  },
});

function AuthLayoutComponent() {
  const auth = useAuth();
  // Initialize PostHog with Clerk user data
  usePostHog();

  const location = useLocation();
  const isPopout = location.pathname.startsWith('/popout/');

  const [toast, setToast] = useState<Toast | null>(null);
  const [isExpanded, setIsExpanded] = useState(!isPopout);
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [, setToastCurrent] = useAtom<Toast | null>(toastAtom);
  const [globalSearch, setGlobalSearch] = useState<GlobalSearchPopupRef | null>(
    null,
  );
  const [, setGLobalSearchCurrent] = useAtom<GlobalSearchPopupRef | null>(
    globalSearchRefAtom,
  );

  useEffect(() => {
    if (globalSearch) {
      setGLobalSearchCurrent(globalSearch);
    }
  }, [globalSearch, setGLobalSearchCurrent]);

  useEffect(() => {
    if (toast) {
      setToastCurrent(toast);
    }
  }, [toast, setToastCurrent]);

  // Show notification if user was signed out due to session conflict
  useSessionConflictNotification(toast);

  // Call all hooks unconditionally (before any return) to satisfy rules of hooks
  const authData = jotaiStore.get(authAtom);
  const userRole = authData?.auth?.orgRole?.split(':')[1] as Role | undefined;
  const isTestUser = userRole === 'test_user';
  const featureFlags = useFeatureFlagsFromClerk();
  const isDynamicDashboardEnabled = isFeatureEnabled(
    featureFlags,
    FEATURE_FLAGS.DYNAMIC_DASHBOARD,
  );
  const isFundManagementEnabled = isFeatureEnabled(
    featureFlags,
    FEATURE_FLAGS.FUND_MANAGEMENT,
  );
  const shouldUseSidebarStatic =
    isTestUser || isDynamicDashboardEnabled || isFundManagementEnabled;

  if (!auth || !auth.isLoaded || (!auth.isSignedIn && auth.isLoaded)) {
    return (
      <SignedOut>
        <RedirectToSignIn signInForceRedirectUrl="/on-logged" />
      </SignedOut>
    );
  }

  return (
    <>
      <WorkflowsNotificationsInitializer />
      <SidebarContext.Provider
        value={{
          isExpanded,
          sidebarWidth: isExpanded ? sidebarWidth : 56,
        }}
      >
        <Toast ref={setToast} />
        {shouldUseSidebarStatic ? (
          <SidebarStatic
            isExpanded={isExpanded}
            onExpandToggle={setIsExpanded}
            sidebarWidth={sidebarWidth}
            onSidebarWidthChange={setSidebarWidth}
          />
        ) : (
          <SidebarStaticV2
            isExpanded={isExpanded}
            onExpandToggle={setIsExpanded}
            sidebarWidth={sidebarWidth}
            onSidebarWidthChange={setSidebarWidth}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <div
            style={{
              width: isExpanded ? `${sidebarWidth}px` : '56px',
              flexShrink: 0,
              transition: 'width 0.3s',
            }}
          >
            {' '}
          </div>
          <div style={{ flex: 1 }}>
            <Outlet />
          </div>
        </div>
        <GlobalSearchPopup
          ref={setGlobalSearch}
          onSearchCallback={() => console.log('on search')}
        />
      </SidebarContext.Provider>
    </>
  );
}
